import React from 'react';
import { Flex, Icon, Text, Title } from '@upsales/components';
import { default as colorMappings } from '@upsales/components/Utils/colorMappings';
import type { Pin, PinBase, PinSpecifics, Translate } from './MapPinTypes';
import type Client from 'App/resources/Model/Client';
import { getJourneyStep } from 'Components/Helpers/journeyStep';
import JourneyStepIcon from 'Components/JourneyIcon/JourneyStepIcon';
import { openDrawer } from 'Services/Drawer';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';

export type CompanyPinClient = Pick<Client, 'id' | 'name' | 'journeyStep' | 'addresses' | 'users' | 'phone'>;

const renderDetails = (pin: Pin<CompanyPinClient>, t: Translate) => {
	const client = pin.data;
	const accountManager = client.users?.[0]?.name;

	return (
		<Flex direction="column" gap="u2">
			<Flex justifyContent="space-between" alignItems="flex-start">
				<Title bold size="md" className="CompanyPin__title clickable">
					{client.name}
				</Title>
				<Text size="sm" color="grey-10" className="CompanyPin__close clickable">
					{'\u2715'}
				</Text>
			</Flex>
			{pin.address ? (
				<Text size="sm" color="grey-11">
					{pin.address}
					{pin.zipcode ? `, ${pin.zipcode}` : ''}
					{pin.city ? ` ${pin.city}` : ''}
				</Text>
			) : null}
			<Flex gap="u2" alignItems="center">
				<JourneyStepIcon id={client.journeyStep} />
				{accountManager ? (
					<Text size="sm" color="grey-11">
						{accountManager}
					</Text>
				) : null}
			</Flex>
			<Flex gap="u2" alignItems="center" className="CompanyPin__actions">
				<Flex
					alignItems="center"
					gap="u1"
					className="CompanyPin__action CompanyPin__action--phonecall clickable"
				>
					<Icon name="phone" color="grey-11" />
					<Text size="sm" color="grey-11">
						{t('clientCard.splitButton.phonecall')}
					</Text>
				</Flex>
				<Flex
					alignItems="center"
					gap="u1"
					className="CompanyPin__action CompanyPin__action--appointment clickable"
				>
					<Icon name="calendar" color="grey-11" />
					<Text size="sm" color="grey-11">
						{t('clientCard.splitButton.appointment')}
					</Text>
				</Flex>
			</Flex>
		</Flex>
	);
};

const getJourneyColor = (journeyStep: string): string => {
	const step = getJourneyStep(journeyStep);
	return step?.color || colorMappings.get('blue');
};

const companyPinBase = (client: CompanyPinClient): PinBase<CompanyPinClient> => {
	const hasReactClientCard = hasSoftDeployAccess('REACT_CLIENT_CARD');
	const journeyColor = getJourneyColor(client.journeyStep);

	return {
		backgroundColor: journeyColor,
		color: colorMappings.get('white'),
		border: journeyColor,
		icon: 'home',
		iconColor: 'white',
		renderDetails,
		clickable: true,
		events: {
			click: [
				{
					className: 'CompanyPin__title',
					handler: (_event, pin) => {
						const clientId = pin.data.id;
						if (hasReactClientCard) {
							Tools.$state.go('react-root-clientCard', { id: clientId, page: 'overview' });
						} else {
							Tools.$state.go('account.dashboard', { id: clientId });
						}
					}
				},
				{
					className: 'CompanyPin__action--phonecall',
					handler: (_event, pin) => {
						openDrawer('CreateCall', {
							client: { id: pin.data.id, name: pin.data.name }
						});
					}
				},
				{
					className: 'CompanyPin__action--appointment',
					handler: (_event, pin) => {
						openEditAppointment({
							appointment: { client: { id: pin.data.id } }
						});
					}
				}
			]
		}
	};
};

const getSpecificsFromClient = (client: CompanyPinClient): PinSpecifics<CompanyPinClient> | null => {
	const addressWithCoords = client.addresses?.find(a => a.latitude && a.longitude);

	if (!addressWithCoords || !addressWithCoords.latitude || !addressWithCoords.longitude) {
		return null;
	}

	return {
		longitude: addressWithCoords.longitude,
		latitude: addressWithCoords.latitude,
		address: addressWithCoords.address || '',
		zipcode: addressWithCoords.zipcode || '',
		city: addressWithCoords.city || '',
		name: client.name,
		data: client
	};
};

const createCompanyPin = (client: CompanyPinClient): Pin<CompanyPinClient> | null => {
	const specifics = getSpecificsFromClient(client);
	if (!specifics) {
		return null;
	}

	return {
		...companyPinBase(client),
		...specifics
	};
};

export default createCompanyPin;
